"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.REQUEST_LIST_SCOPES = exports.setLeaveManagedRequestSchema = exports.copyLeaveQuotasRequestSchema = exports.saveLeaveQuotaRequestSchema = exports.leaveQuotaSchema = exports.updateLeaveTypeRequestSchema = exports.createLeaveTypeRequestSchema = exports.leaveTypeSchema = exports.LEAVE_ATTACHMENT_RULES = exports.LEAVE_NOTE_RULES = exports.LEAVE_CARRY_OVERS = exports.LEAVE_QUOTA_SOURCES = void 0;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Leave types and quotas — C46, Phase 3.                                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The vocabulary a request is made against, and how many days of it each person gets in a year.
 * The requests themselves are C47.
 *
 * ⚠️⚠️ **WHOLE DAYS, EVERYWHERE — P3-2.** There are no half-days in this phase: a request is a
 * date range, its cost is the working days in it, and an entitlement is an integer. Every number
 * here is `z.number().int()` for that reason and not by accident.
 */
/**
 * Whether a type draws on a quota or is unlimited.
 *
 * ⚠️⚠️ **THERE WAS A THIRD, `none`, AND IT WAS `unlimited` UNDER ANOTHER NAME — `0118`.** The
 * comment defended a difference of intent — *unlimited* is tracked with nothing to run out of,
 * *none* is recorded and never counted — that nothing in the system ever acted on:
 * `leave-request.service.ts` branches on `=== 'quota'` in both places it cares, so the two reached
 * identical code. Reported as *"feels the same"*, and it was.
 */
/**
 * ⚠️⚠️ **`overtime` IS THE THIRD ANSWER TO ONE QUESTION, NOT A FLAG ON THE FIRST.** Where does a
 * type's entitlement come from: an administrator grants it (`quota`), there is no frame at all
 * (`unlimited`), or **it is earned by working** — days credited from an approved overtime claim.
 * Feedback round 4 item 25, shaped by the product owner on 19 Sep.
 *
 * ⚠️ **A type is only a credit destination while it says so**, and several may. The approver picks
 * between them; the first by sort order is pre-selected.
 *
 * ⚠️ `none` is in the database and not here — `0108` shipped it and nothing offers it.
 */
exports.LEAVE_QUOTA_SOURCES = ['quota', 'unlimited', 'overtime'];
/**
 * ⚠️ **`max5` is a number in an enum, and it is the mockup's own vocabulary** rather than a scale
 * somebody can dial. A workspace wanting *"at most seven"* is a fourth value or a column; inventing
 * the column now would be a knob against a requirement nobody has stated.
 */
exports.LEAVE_CARRY_OVERS = ['none', 'full', 'max5'];
exports.LEAVE_NOTE_RULES = ['optional', 'required', 'hidden'];
exports.LEAVE_ATTACHMENT_RULES = ['optional', 'required'];
exports.leaveTypeSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** The public identifier, `leave_type_<8 hex>`, minted by the database (`0194`). */
    identifier: (0, identifier_1.identifierSchema)('leaveType'),
    name: zod_1.z.string(),
    icon: zod_1.z.string(),
    active: zod_1.z.boolean(),
    /** May a request of this type be filed for the past. Sickness is why this exists. */
    backdate: zod_1.z.boolean(),
    quotaSource: zod_1.z.enum(exports.LEAVE_QUOTA_SOURCES),
    carryOver: zod_1.z.enum(exports.LEAVE_CARRY_OVERS),
    /** 1–12, and `null` exactly when nothing carries. The column check says the same. */
    carryToMonth: zod_1.z.number().int().nullable(),
    noteRule: zod_1.z.enum(exports.LEAVE_NOTE_RULES),
    attachmentRule: zod_1.z.enum(exports.LEAVE_ATTACHMENT_RULES),
    sortOrder: zod_1.z.number().int(),
    /** How many quota rows point at it — what makes deleting a type legible before it happens. */
    quotaCount: zod_1.z.number().int().nonnegative(),
    version: zod_1.z.number().int(),
});
exports.createLeaveTypeRequestSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(120),
    icon: zod_1.z.string().trim().min(1).max(60).optional(),
    active: zod_1.z.boolean().optional(),
    backdate: zod_1.z.boolean().optional(),
    quotaSource: zod_1.z.enum(exports.LEAVE_QUOTA_SOURCES),
    carryOver: zod_1.z.enum(exports.LEAVE_CARRY_OVERS),
    carryToMonth: zod_1.z.number().int().min(1).max(12).nullable().optional(),
    noteRule: zod_1.z.enum(exports.LEAVE_NOTE_RULES),
    attachmentRule: zod_1.z.enum(exports.LEAVE_ATTACHMENT_RULES),
});
exports.updateLeaveTypeRequestSchema = exports.createLeaveTypeRequestSchema.extend({
    version: zod_1.z.number().int().nonnegative(),
});
/**
 * One cell of the grid.
 *
 * ⚠️⚠️ **NO ROW MEANS NO ENTITLEMENT, WHICH IS NOT THE SAME AS ZERO.** Somebody whose leave this
 * workspace does not manage has no rows at all; somebody entitled to nothing this year has a row
 * saying `0`. The settings screen names the first group so the empty cells read as a decision.
 */
exports.leaveQuotaSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** The public identifier, `leave_quota_<8 hex>`, minted by the database (`0194`). */
    identifier: (0, identifier_1.identifierSchema)('leaveQuota'),
    memberId: zod_1.z.string(),
    memberName: zod_1.z.string(),
    leaveTypeId: zod_1.z.string(),
    year: zod_1.z.number().int(),
    days: zod_1.z.number().int(),
    /**
     * ⚠️ **Written by the carry-over chore and by nothing else** — P3-8. The grid shows them beside
     * the entitlement and never edits them: they are what last year left, not a number anybody types.
     */
    carriedDays: zod_1.z.number().int().nonnegative(),
    carriedExpiresOn: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    /**
     * ⚠️⚠️ **WHAT IS SPENT AND WHAT IS IN FLIGHT, AND THEY ARE COUNTED APART.** A request awaiting a
     * decision is neither spent nor free — `leave-request.service.ts`' balance says the same, for the
     * same reason: a grid that folded pending into used would let an admin cut somebody's
     * entitlement to a figure a decision is about to exceed, and one that ignored it would let them
     * cut it to a figure that is already over.
     *
     * ⚠️ **Computed, never stored.** A day's cost comes from the day kernel (C44), so a week holding
     * a public holiday costs four; freezing either figure would leave the grid disagreeing with the
     * calendar the moment a holiday is imported.
     */
    usedDays: zod_1.z.number().int().nonnegative(),
    pendingDays: zod_1.z.number().int().nonnegative(),
    version: zod_1.z.number().int(),
});
/**
 * ⚠️ **One verb that replaces, like the schedule assignment's.** A person has one entitlement of a
 * type in a year; whether a row exists yet is the server's question rather than something a form
 * finds out with a round trip first. `days: 0` is an entitlement of nothing and `null` removes the
 * row — *"this does not apply to them"* — which are different answers.
 */
exports.saveLeaveQuotaRequestSchema = zod_1.z.object({
    memberId: zod_1.z.string(),
    leaveTypeId: zod_1.z.string(),
    year: zod_1.z.number().int().min(2000).max(2100),
    days: zod_1.z.number().int().min(0).max(365).nullable(),
});
/**
 * 17 Oct 2026 · *Másolás tavalyról* — the chosen people's frames for `year`, copied from the year
 * before, type by type. A type they had no row for last year is left as it is.
 */
exports.copyLeaveQuotasRequestSchema = zod_1.z.object({
    year: zod_1.z.number().int().min(2000).max(2100),
    memberIds: zod_1.z.array(zod_1.z.uuid()).min(1).max(500),
});
/** Whether this workspace manages this person's leave at all. The person screen's own row. */
exports.setLeaveManagedRequestSchema = zod_1.z.object({ managed: zod_1.z.boolean() });
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHICH REQUEST LIST — AND IT IS THE CALLER'S CHOICE, NOT A PERMISSION'S.│
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Three lists, and they answer three different questions:
 *
 * | | |
 * | --- | --- |
 * | `mine`   | *Saját kérelmeim* — what I have asked for |
 * | `decide` | *Elbírálandó kérelmek* — what I am the reviewer for, and nothing else |
 * | `all`    | *Kérelmek* — every request in the workspace, behind `leave.view` |
 *
 * ⚠️⚠️ **`decide` AND `all` USED TO BE THE SAME REQUEST.** `mine=false` returned the whole
 * workspace to anybody holding `leave.view` and a manager's own reports to anybody who did not —
 * so the scope was decided by the reader's permissions rather than by the screen they opened, and
 * a lead who ALSO held `leave.view` had no way to see just their own queue. Reported by the
 * product owner: *"the new elbírálandó is a lead view where the lead can see only the requests
 * they are the ones supposed to review, in case they don't have permission to view the global
 * list."*
 *
 * ⚠️ **`decide` needs no permission at all** — being somebody's manager, or the named approver on
 * an overtime claim, IS the entitlement. That is the whole point of the view.
 */
exports.REQUEST_LIST_SCOPES = ['mine', 'decide', 'all'];
//# sourceMappingURL=leave.js.map